import React, { useState, useEffect, useRef } from 'react';
import { createChart } from 'lightweight-charts';
import { useDispatch, useSelector } from "react-redux";
// import './ValueOfTime.css';
// import config from '../config/config.json';
import { TextField, Button, Box, Stack, useTheme, Typography } from '@mui/material';
import { tokens } from "../../theme";
import Preloading from '../../components/Preloading';
import { useIsMobile, useIsPortraitTablet, useIsTablet } from '../../components/hooks';


const ValueOfTime = () => {

    const isMobile = useIsMobile();
    const isTablet = useIsTablet();
    const isPTablet = useIsPortraitTablet();

    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [symbol, setSymbol] = useState('');
    const [date, setDate] = useState('');
    const [dailyData, setDailyData] = useState([]);
    const [intradayData, setIntradayData] = useState([]);
    const [loading, setLoading] = useState(false);

    const priceChartRef = useRef();
    const exceedFreqChartRef = useRef();
    const intradayPriceChartRef = useRef();
    const exceedValueChartRef = useRef();
    const intradayNetChartRef = useRef();

    const csrfToken = useSelector((state) => state.csrfToken);

    const clearCharts = () => {
        [priceChartRef, exceedFreqChartRef, exceedValueChartRef, intradayPriceChartRef, intradayNetChartRef].forEach(ref => {
            if (ref.current && ref.current._chart) {
                ref.current._chart.remove();
                ref.current._chart = null;
            }
        });
    };
    // แปลง time จาก API ให้เป็น unix seconds ที่ lightweight-charts ใช้ได้
    const toUnixSeconds = (t) => {
        if (!t) return undefined;
      
        if (typeof t === 'number') {
          return t > 1e12 ? Math.floor(t / 1000) : t;
        }
      
        if (typeof t === 'string') {
          let s = t.trim();
      
          // กรณีเป็นวันที่อย่างเดียว "2021-08-27"
          if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
            s = s + 'T00:00:00Z';
          } else if (!s.includes('T')) {
            // กรณี "2025-11-28 10:20:00"
            s = s.replace(' ', 'T') + 'Z';
          }
      
          const d = new Date(s);
          const ts = Math.floor(d.getTime() / 1000);
          return Number.isNaN(ts) ? undefined : ts;
        }
      
        return undefined;
      };
      
    
  

    const fetchDailyData = async (symbol) => {
        try {
          const response = await fetch(
            `${window.config.base_api_url}/vot?symbol=${symbol}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'xsrf-token': csrfToken,
              },
            }
          );
      
          // พยายามอ่าน JSON ถ้าอ่านไม่ได้ให้เป็น object ว่าง
          const json = await response.json().catch(() => ({}));
          console.log('VOT Daily response data: ', json);
      
          // ถ้า backend บอก status=false หรือ HTTP ไม่โอเค → ถือว่าไม่มี data
          if (!response.ok || json.status === false) {
            console.warn('Daily HTTP error', response.status, json);
            return [];
          }
      
          // ถ้า data เป็น array → ใช้เลย
          if (Array.isArray(json.data)) {
            return json.data;
          }
      
          console.warn('Unexpected daily response shape: ', json);
          return [];
        } catch (error) {
          console.error('Error fetching daily data:', error);
          // error ใด ๆ ให้คืน [] เพื่อไม่ให้หน้าแตก
          return [];
        }
      };
      
      const fetchIntradayData = async (symbol, date) => {
        try {
          const url = `${window.config.base_api_url}/vot?symbol=${symbol}&date=${date}`;
          const response = await fetch(url, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "xsrf-token": csrfToken,
            },
          });
      
          const json = await response.json().catch(() => null);
      
          if (!response.ok || json?.status === false) {
            console.warn("Intraday HTTP error", {
              status: response.status,
              message: json?.message,
              code: json?.code,
              url,
            });
            return [];
          }
      
          if (Array.isArray(json?.data)) return json.data;
      
          console.warn("Unexpected intraday response shape:", json);
          return [];
        } catch (error) {
          console.warn("Error fetching intraday data:", error);
          return [];
        }
      };
      
      

      const fetchData = async () => {
        if (!symbol || !date) {
          console.error('Both Symbol and Date are required');
          return;
        }
      
        setLoading(true);
        try {
          clearCharts();
      
          const [dailyResponse, intradayResponse] = await Promise.all([
            fetchDailyData(symbol),
            fetchIntradayData(symbol, date),
          ]);
      
          // ทั้งสองตัวจะเป็น array เสมอ (อาจว่าง)
          setDailyData(dailyResponse);
          setIntradayData(intradayResponse);
      
          console.log('Fetched data: ', {
            dailyResponse,
            intradayResponse,
          });
        } catch (error) {
          console.error('Error fetching data:', error);
          setDailyData([]);
          setIntradayData([]);
        } finally {
          setLoading(false);
        }
      };
      

    // Utility function to sort data by time in ascending order
    const sortByTimeAscending = (data) => {
        return data.sort((a, b) => a.time - b.time);
    };

    // Check if both inputs are filled
    const areInputsFilled = () => symbol && date;

    // Sync visible range between two charts
    const syncVisibleRange = (chart1, chart2) => {
        chart1.timeScale().subscribeVisibleLogicalRangeChange((timeRange) => {
            chart2.timeScale().setVisibleLogicalRange(timeRange);
        });

        chart2.timeScale().subscribeVisibleLogicalRangeChange((timeRange) => {
            chart1.timeScale().setVisibleLogicalRange(timeRange);
        });
    };

    // Sync crosshair between two charts
    const getCrosshairDataPoint = (series, param) => {
        if (!param.time) {
            return null;
        }
        const dataPoint = param.seriesData.get(series);
        return dataPoint || null;
    };

    const syncCrosshair = (chart, series, dataPoint) => {
        if (dataPoint) {
            chart.setCrosshairPosition(dataPoint.value, dataPoint.time, series);
        } else {
            chart.clearCrosshairPosition();
        }
    };

    const syncCrosshairMove = (chart1, series1, chart2, series2) => {
        chart1.subscribeCrosshairMove((param) => {
            const dataPoint = getCrosshairDataPoint(series1, param);
            syncCrosshair(chart2, series2, dataPoint);
        });

        chart2.subscribeCrosshairMove((param) => {
            const dataPoint = getCrosshairDataPoint(series2, param);
            syncCrosshair(chart1, series1, dataPoint);
        });
    };

    const createChartContainer = (container, options) => {
        // const chart = createChart(container, options);
        // container._chart = chart; // Save reference to prevent duplicates
        // return chart;

        if (!container) return;

        // ตรวจสอบว่ามี _chart อยู่แล้วหรือไม่ ถ้ามีให้ลบก่อน
        if (container._chart) {
            container._chart.remove();
        }

        // สร้าง wrapper ป้องกันปัญหากราฟซ้อน
        const wrapper = document.createElement('div');
        wrapper.style.width = '100%';
        wrapper.style.height = '100%';
        wrapper.style.borderRadius = '12px';
        wrapper.style.overflow = 'hidden';
        wrapper.style.position = 'relative'; // ✅ ป้องกันซ้อนกันมั่ว
        wrapper.style.backgroundColor = theme.palette.table.odd;

        container.innerHTML = ''; // ✅ ล้าง element เก่าก่อนใส่ wrapper
        container.appendChild(wrapper);

        const chart = createChart(wrapper, options);
        container._chart = chart; // ✅ ป้องกันสร้างซ้ำ

        return chart;
    };

    // Create chart for price data
    // รับ data เป็น [{ time, value }]
const createPriceChart = (container, data) => {
    if (!container) return;
  
    container.innerHTML = '';
    const wrapper = document.createElement('div');
    wrapper.style.width = '100%';
    wrapper.style.height = '100%';
    wrapper.style.borderRadius = '12px';
    wrapper.style.overflow = 'hidden';
    wrapper.style.backgroundColor = theme.palette.table.odd;
    container.appendChild(wrapper);
  
    const chart = createChartContainer(wrapper, {
      width: container.clientWidth,
      height: container.clientHeight,
      timeScale: { rightOffset: 5 },
    });
  
    chart.applyOptions({
      layout: {
        attributionLogo: false,
        background: { type: 'solid', color: theme.palette.background.default },
        textColor: theme.palette.text.primary,
      },
                     grid: {
         horzLines: { color: theme.palette.divider },
         vertLines: { color: theme.palette.divider },
      },
      watermark: {
        visible: true,
        fontSize: 24,
        horzAlign: 'center',
        vertAlign: 'top',
        color: theme.palette.chart.watermark,
        text: symbol.toUpperCase(),
      },
    });
  
    const series = chart.addLineSeries({ color: '#95fe51', priceLineVisible: false });
    console.log('price points sample', data.slice(0, 5));
    series.setData(data);
  
    return { chart, series };
  };
  
  const createExceedFreqChart = (container, data) => {
    if (!container) return;
  
    container.innerHTML = '';
    const wrapper = document.createElement('div');
    wrapper.style.width = '100%';
    wrapper.style.height = '100%';
    wrapper.style.borderRadius = '12px';
    wrapper.style.overflow = 'hidden';
    wrapper.style.backgroundColor = theme.palette.table.odd;
    container.appendChild(wrapper);
  
    const chart = createChartContainer(wrapper, {
      width: container.clientWidth,
      height: container.clientHeight,
      timeScale: { rightOffset: 5 },
    });
  
    chart.applyOptions({
      layout: {
        attributionLogo: false,
        background: { type: 'solid', color: theme.palette.background.default },
        textColor: theme.palette.text.primary,
      },
                     grid: {
         horzLines: { color: theme.palette.divider },
         vertLines: { color: theme.palette.divider },
      },
      watermark: {
        visible: true,
        fontSize: 24,
        horzAlign: 'center',
        vertAlign: 'top',
        color: theme.palette.chart.watermark,
        text: symbol.toUpperCase(),
      },
    });
  
    const series = chart.addLineSeries({
      color: '#5efee8',
      priceLineVisible: false,
    });
    console.log('freq points sample', data.slice(0, 5));
    series.setData(data);
  
    return { chart, series };
  };
  
  const createExceedValueChart = (container, data) => {
    if (!container) return;
  
    container.innerHTML = '';
    const wrapper = document.createElement('div');
    wrapper.style.width = '100%';
    wrapper.style.height = '100%';
    wrapper.style.borderRadius = '12px';
    wrapper.style.overflow = 'hidden';
    wrapper.style.backgroundColor = theme.palette.table.odd;
    container.appendChild(wrapper);
  
    const chart = createChartContainer(wrapper, {
      width: container.clientWidth,
      height: container.clientHeight,
      timeScale: { rightOffset: 5 },
    });
  
    chart.applyOptions({
      layout: {
        attributionLogo: false,
        background: { type: 'solid', color: theme.palette.background.default },
        textColor: theme.palette.text.primary,
      },
                     grid: {
         horzLines: { color: theme.palette.divider },
         vertLines: { color: theme.palette.divider },
      },
      watermark: {
        visible: true,
        fontSize: 24,
        horzAlign: 'center',
        vertAlign: 'top',
        color: theme.palette.chart.watermark,
        text: symbol.toUpperCase(),
      },
    });
  
    const series = chart.addLineSeries({
      color: '#ffdf4f',
      priceLineVisible: false,
      priceFormat: {
        type: 'volume',
        minMove: 1,
        precision: 2,
      },
    });
    console.log('value points sample', data.slice(0, 5));
    series.setData(data);
  
    return { chart, series };
  };
  

    // Add this function to convert the timestamp to UNIX
    const parseDateString = (dateString) => toUnixSeconds(dateString);


    // Create chart for intraday price
    const createIntradayPriceChart = (container, data, exceedDate) => {
        if (!container) return;
        if (container._chart) container._chart.remove();
      
        const chart = createChart(container, {
          width: container.clientWidth,
          height: container.clientHeight,
          timeScale: { timeVisible: true, secondsVisible: true },
        });
        container._chart = chart;
      
        chart.applyOptions({
          layout: {
            attributionLogo: false,
            background: { type: 'solid', color: theme.palette.background.default },
            textColor: theme.palette.text.primary,
          },
                         grid: {
             horzLines: { color: theme.palette.divider },
             vertLines: { color: theme.palette.divider },
          },
          watermark: { visible: true, fontSize: 24, color: theme.palette.chart.watermark, text: symbol.toUpperCase() },
        });
      
        const series = chart.addLineSeries({ color: "#df82ff", priceLineVisible: false });
        series.setData(data);
        return { chart, series };
      };
      
      const createIntradayNetChart = (container, data, exceedDate) => {
        if (!container) return;
        if (container._chart) container._chart.remove();
      
        const chart = createChart(container, {
          width: container.clientWidth,
          height: container.clientHeight,
          timeScale: { timeVisible: true, secondsVisible: true },
        });
        container._chart = chart;
      
        chart.applyOptions({
          layout: {
            attributionLogo: false,
            background: { type: 'solid', color: theme.palette.background.default },
            textColor: theme.palette.text.primary,
          },
                         grid: {
             horzLines: { color: theme.palette.divider },
             vertLines: { color: theme.palette.divider },
          },
          watermark: { visible: true, fontSize: 24, color: theme.palette.chart.watermark, text: symbol.toUpperCase() },
        });
      
        const series = chart.addLineSeries({ color: "#a8d2ff", priceLineVisible: false });
        series.setData(data);
        return { chart, series };
      };
      

    useEffect(() => {
        clearCharts();
      
        if (!Array.isArray(dailyData)) {
          console.log('dailyData is not array -> ', dailyData);
          return;
        }
      
        if (!dailyData.length) {
          console.log('No dailyData to plot');
          return;
        }
      
        console.log('dailyData sample', dailyData[0]); // { date, n_exceed, val_exceed }
      
        const sortByTime = (a, b) => a.time - b.time;
      
        // 1) ใช้ val_exceed เป็น series สำหรับกราฟ "Price" (daily value)
        const pricePoints = dailyData
          .map((e) => ({
            time: toUnixSeconds(e.date || e.time),
            value: e.val_exceed ?? e.n_exceed ?? null,
          }))
          .filter((p) => p.time !== undefined && p.value != null)
          .sort(sortByTime);
      
        // 2) กราฟ Frequency Exceed ใช้ n_exceed
        const exceedFreqPoints = dailyData
          .map((e) => ({
            time: toUnixSeconds(e.date || e.time),
            value: e.n_exceed ?? null,
          }))
          .filter((p) => p.time !== undefined && p.value != null)
          .sort(sortByTime);
      
        // 3) กราฟ Exceed Value ใช้ val_exceed
        const exceedValuePoints = dailyData
          .map((e) => ({
            time: toUnixSeconds(e.date || e.time),
            value: e.val_exceed ?? null,
          }))
          .filter((p) => p.time !== undefined && p.value != null)
          .sort(sortByTime);
      
        console.log('pricePoints sample', pricePoints.slice(0, 5));
        console.log('exceedFreqPoints sample', exceedFreqPoints.slice(0, 5));
        console.log('exceedValuePoints sample', exceedValuePoints.slice(0, 5));
      
        const { chart: priceChart } =
          createPriceChart(priceChartRef.current, pricePoints);
        const { chart: freqChart } =
          createExceedFreqChart(exceedFreqChartRef.current, exceedFreqPoints);
        const { chart: valueChart } =
          createExceedValueChart(exceedValueChartRef.current, exceedValuePoints);
      
        // sync เฉพาะ 3 กราฟ daily
        if (priceChart && freqChart) syncVisibleRange(priceChart, freqChart);
        if (priceChart && valueChart) syncVisibleRange(priceChart, valueChart);
        if (freqChart && valueChart) syncVisibleRange(freqChart, valueChart);
      }, [dailyData]);
      useEffect(() => {
        if (!Array.isArray(intradayData)) return;
        if (!intradayData.length) return;
      
        console.log("intradayData sample", intradayData[0]);
        const sortByTime = (a, b) => a.time - b.time;
      
        // ✅ Intraday Price: ถ้าไม่มี last ให้ใช้ net_val หรือ val_exceed แทน
        const intradayPricePoints = intradayData
          .map(row => ({
            time: toUnixSeconds(row.time || row.datetime || row.date_time),
            value: row.last ?? row.price ?? row.val_exceed ?? row.net_val ?? 0,
          }))
          .filter(p => p.time !== undefined && p.value != null)
          .sort(sortByTime);
      
        // ✅ Intraday Net Value: ใช้ net_val โดยตรง
        const intradayNetPoints = intradayData
          .map(row => ({
            time: toUnixSeconds(row.time || row.datetime || row.date_time),
            value: row.net_val ?? row.val_exceed ?? 0,
          }))
          .filter(p => p.time !== undefined && p.value != null)
          .sort(sortByTime);
      
        console.log("intradayPricePoints sample", intradayPricePoints.slice(0, 5));
        console.log("intradayNetPoints sample", intradayNetPoints.slice(0, 5));
      
        const exceedDate = { up: [], down: [] };
        createIntradayPriceChart(intradayPriceChartRef.current, intradayPricePoints, exceedDate);
        createIntradayNetChart(intradayNetChartRef.current, intradayNetPoints, exceedDate);
      }, [intradayData]);
      
      
      
      
      {!loading && Array.isArray(dailyData) && dailyData.length === 0 && (
        <Typography sx={{ textAlign: 'center', mt: 2 }}>
          ไม่มีข้อมูลสำหรับสัญลักษณ์ {symbol.toUpperCase()} และวันที่ {date}
        </Typography>
      )}
      
      
      
    return (
        <div style={{ fontFamily: 'Arial, sans-serif', maxWidth: '1800px', paddingLeft: '20px', paddingRight: '20px' }}>

            <Typography variant='h3' sx={{ fontWeight: 'bold', padding: '10px 0 20px 0', display: 'flex', justifyContent: 'center' }}>Value of Time

            </Typography>
            {/* <div className='vot-header-input'>
                <div className='vot-input'>
                    <label>Symbol:</label>
                    <input
                        value={symbol}
                        onChange={(e) => setSymbol(e.target.value)}
                        placeholder="Enter symbol"
                    />
                </div>

                <div className='vot-input'>
                    <label>Select date:</label>
                    <input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                    />
                </div>

                <button onClick={fetchData} disabled={!areInputsFilled()}>Submit</button>
            </div> */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    marginBottom: 2,
                }}
            >
                <Box>
                    <TextField
                        label="Symbol"
                        value={symbol}
                        onChange={(e) => setSymbol(e.target.value)}
                        placeholder="Enter symbol"
                        variant="outlined"
                        fullWidth
                        InputLabelProps={{
                            shrink: true,
                        }}
                        sx={{
                            '& .MuiInputBase-input': {
                                padding: "10px",
                            },
                        }}
                    />
                </Box>
                <Box>
                    <TextField
                        label="Select Date"
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        variant="outlined"
                        fullWidth
                        sx={{
                            '& .MuiInputBase-input': {
                                padding: "10px",
                            },
                        }}
                    />
                </Box>

                <Button
                    variant="contained"
                    onClick={fetchData}
                    disabled={!areInputsFilled()}
                    sx={{
                        padding: "10px",
                        backgroundColor: colors.blueAccent[500],
                    }}
                >
                    Submit
                </Button>
            </Box>
            <Preloading open={loading} />
            {!loading && Array.isArray(dailyData) && dailyData.length === 0 && (
  <Typography sx={{ textAlign: 'center', mt: 2 }}>
    ไม่มีข้อมูลสำหรับสัญลักษณ์ {symbol.toUpperCase()} และวันที่ {date}
  </Typography>
)}
            {loading && (
                <p style={{
                    fontSize: '24px',
                    fontWeight: 'bold',
                    color: '#000000',
                    textAlign: 'center',
                    marginTop: '20px'
                }}>
                    {/* Loading... */}
                </p>
            )}

            <Box sx={{
                gap: '10px', display: 'flex', flexDirection: 'column',
                //  margin: '0 10px 0 10px', padding: 2 , backgroundColor: theme.palette.background.sidebar, boxShadow: theme.palette.shadow.primary
            }}>

                <Box>
                    <Typography variant='h5' sx={{ backgroundColor: theme.palette.background.card, padding: 0.75, width: 'fit-content', borderRadius: '5px', marginBottom: '5px' }}>
                        Price
                    </Typography>
                    <div
                        ref={priceChartRef}
                        style={{
                            position: 'relative',
                            height: (isMobile || isTablet) ? '200px' : '230px',
                            width: '100%',
                            backgroundColor: theme.palette.table.odd,
                            borderRadius: '10px'
                        }}
                    >
                        {/* <div style={{ position: "absolute", top: '2%', left: '2%', zIndex: 20 }}>Price</div> */}
                    </div>
                </Box>



                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <Box>
                        <Typography variant='h5' sx={{ backgroundColor: theme.palette.background.card, padding: 0.75, width: 'fit-content', borderRadius: '5px', marginBottom: '5px' }}>
                            Frequency Exceed
                        </Typography>
                        <div
                            ref={exceedFreqChartRef}
                            style={{
                                position: 'relative',
                                height: (isMobile || isTablet) ? '200px' : '230px',
                                width: '100%',
                                backgroundColor: theme.palette.table.odd,
                                borderRadius: '10px'
                            }}
                        >
                            {/* <div style={{ position: "absolute", top: '2%', left: '2%', zIndex: 20 }}>Frequency Exceed</div> */}
                        </div>
                    </Box>
                    <Box>
                        <Typography variant='h5' sx={{ backgroundColor: theme.palette.background.card, padding: 0.75, width: 'fit-content', borderRadius: '5px', marginBottom: '5px' }}>
                            Intraday Price
                        </Typography>
                        <div
                            ref={intradayPriceChartRef}
                            style={{
                                position: 'relative',
                                height: (isMobile || isTablet) ? '200px' : '230px',
                                width: '100%',
                                backgroundColor: theme.palette.table.odd,
                                borderRadius: '10px'
                            }}
                        >
                            {/* <div style={{ position: "absolute", top: '2%', left: '2%', zIndex: 20 }}>Intraday Price</div> */}
                        </div>
                    </Box>


                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <Box>
                        {/* <Box sx={{}}>
                        Exceed Value
                    </Box> */}
                        <Typography variant='h5' sx={{ backgroundColor: theme.palette.background.card, padding: 0.75, width: 'fit-content', borderRadius: '5px', marginBottom: '5px' }}>
                            Exceed Value
                        </Typography>
                        <div
                            ref={exceedValueChartRef}
                            style={{
                                position: 'relative',
                                height: (isMobile || isTablet) ? '200px' : '230px',
                                width: '100%',
                                backgroundColor: theme.palette.table.odd,
                                borderRadius: '10px'
                            }}
                        >
                            {/* <div style={{ position: "absolute", top: '2%', left: '2%', zIndex: 20 }}>Exceed Value</div> */}
                        </div>
                    </Box>

                    <Box>
                        <Typography variant='h5' sx={{ backgroundColor: theme.palette.background.card, padding: 0.75, width: 'fit-content', borderRadius: '5px', marginBottom: '5px' }}>
                            Intraday Net Value
                        </Typography>
                        <div
                            ref={intradayNetChartRef}
                            style={{
                                position: 'relative',
                                height: (isMobile || isTablet) ? '200px' : '230px',
                                width: '100%',
                                backgroundColor: theme.palette.table.odd,
                                borderRadius: '10px'
                            }}
                        >
                            {/* <div style={{ position: "absolute", top: '2%', left: '2%', zIndex: 20 }}>Intraday Net Value</div> */}
                        </div>
                    </Box>

                </div>

            </Box>



        </div>
    );
};

export default ValueOfTime;
